// AI Ghostwriter Pro - About Page
import React from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import { BookOpen, Users, Shield, Award } from 'lucide-react';

export function GhostwriterAbout() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SeoHead
        title="About | AI Ghostwriter Pro - Professional AI Book Writing"
        description="AI Ghostwriter Pro is a professional AI-powered book writing service by Web-Tech Services. Based in Arlington, Texas. Part of the UPost author platform."
        canonical="https://aighostwriterpro.com/about"
        og={{ type: 'website', url: 'https://aighostwriterpro.com/about', title: 'About AI Ghostwriter Pro', description: 'Professional AI book writing service by Web-Tech Services.', siteName: 'AI Ghostwriter Pro' }}
        twitter={{ card: 'summary', title: 'About AI Ghostwriter Pro', description: 'Professional AI book writing service.' }}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="about-heading">
            About AI Ghostwriter Pro
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            We believe every author deserves to see their book published. AI technology makes it possible.
          </p>
        </div>

        {/* Mission */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              AI Ghostwriter Pro was built to solve a simple problem: writing a book is hard, expensive, and time-consuming. Traditional ghostwriters charge $5,000-$50,000 and take months. Most aspiring authors never finish their first draft.
            </p>
            <p className="text-gray-300 leading-relaxed">
              We use advanced AI to deliver publication-ready manuscripts in days, not months — at a fraction of the cost. Our packages start at $497, making professional book writing accessible to everyone with a story to tell.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto mb-16">
          {[
            { icon: BookOpen, title: 'Quality First', desc: 'Every manuscript goes through multiple AI writing passes and quality checks. Publication-ready, every time.' },
            { icon: Users, title: 'Author-Owned', desc: 'You own 100% of the rights. No royalty splits, no attribution. Your name, your book, your earnings.' },
            { icon: Shield, title: 'Original Content', desc: 'Every book is generated uniquely for you. Verified plagiarism-free with professional detection tools.' },
            { icon: Award, title: 'Satisfaction Guaranteed', desc: 'Not happy with your outline? Revise it. Not satisfied after 3 chapters? Full refund, no questions asked.' }
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6 text-center">
                <Icon className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Company Info */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Who We Are</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              AI Ghostwriter Pro is a product of <strong className="text-white">Web-Tech Services</strong>, based in Arlington, Texas. We're part of the <a href="https://upost.com" className="text-purple-400 hover:underline">UPost</a> author success platform, which provides AI-powered tools for writers at every stage of their journey.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Our team combines expertise in artificial intelligence, creative writing, and digital publishing to help authors turn ideas into published books — faster and more affordably than ever before.
            </p>
            <div className="mt-6 pt-6 border-t border-slate-700 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Headquarters</p>
                <p className="text-white">Arlington, Texas, USA</p>
              </div>
              <div>
                <p className="text-gray-500">Parent Company</p>
                <p className="text-white">Web-Tech Services</p>
              </div>
              <div>
                <p className="text-gray-500">Platform</p>
                <a href="https://upost.com" className="text-purple-400 hover:underline">UPost.com</a>
              </div>
              <div>
                <p className="text-gray-500">Contact</p>
                <Link to="/contact" className="text-purple-400 hover:underline">Get in touch</Link>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Write Your Book?</h2>
          <div className="flex justify-center gap-4">
            <Link to="/how-it-works">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                How It Works
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GhostwriterAbout;
